import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';

type Pkg = {
  id: string;
  label: string | null;
  price: number;
  currency: string;
  credits: number;
};

/** Customer credit-bundle selector (10 / 25 / 50 …). The bundles are real,
 *  admin-editable rows; the purchase itself is a placeholder — there is no billing
 *  flow yet, so "Select" only acknowledges the choice. Renders nothing when no
 *  active packages exist, so it never shows an empty shell. */
export function PackageSelector() {
  const { t, i18n } = useTranslation();
  const crud = useClientQueries(schema);
  const list = crud.creditPackage.useFindMany({
    where: { active: true },
    orderBy: { sortOrder: 'asc' },
  });
  const [selected, setSelected] = useState<string | null>(null);

  const rows = (list.data ?? []) as Pkg[];
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const money = (price: number, currency: string) => {
    try {
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(price);
    } catch {
      return `${price} ${currency}`;
    }
  };

  if (!list.isPending && rows.length === 0) return null;

  function onSelect() {
    // No billing flow yet — the bundles are fictitious pricing. Acknowledge the
    // choice honestly instead of pretending to charge.
    toast.info(t('subscription.purchaseSoon'));
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div>
        <h2 className="text-sm font-medium">{t('subscription.packages')}</h2>
        <p className="text-xs text-muted-foreground">{t('subscription.packagesSubtitle')}</p>
      </div>

      {list.isPending ? (
        <p className="py-2 text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            {rows.map((pkg) => {
              const isSel = selected === pkg.id;
              return (
                <button
                  key={pkg.id}
                  type="button"
                  onClick={() => setSelected(pkg.id)}
                  aria-pressed={isSel}
                  className={`relative rounded-lg border p-3 text-center transition-colors ${
                    isSel
                      ? 'border-foreground bg-muted'
                      : 'border-border hover:border-foreground/40'
                  }`}
                >
                  {isSel ? (
                    <Check className="absolute right-1.5 top-1.5 h-3.5 w-3.5" aria-hidden />
                  ) : null}
                  {pkg.label ? (
                    <div className="mb-0.5 truncate text-[11px] text-muted-foreground">{pkg.label}</div>
                  ) : null}
                  <div className="text-lg font-semibold tabular-nums">{money(pkg.price, pkg.currency)}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                    {t('subscription.packageCredits', { credits: nf.format(pkg.credits) })}
                  </div>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={onSelect}
            disabled={!selected}
            className="btn-primary w-full"
          >
            {t('subscription.select')}
          </button>
        </>
      )}
    </div>
  );
}
